import { useEffect, useRef } from 'react';
import maplibregl, { Marker, Popup } from 'maplibre-gl';
import { useMapStore } from '@/stores/mapStore';
import { useSearchStore } from '@/stores/searchStore';
import { useRoutingStore } from '@/stores/routingStore';
import type { SearchResult, Coordinates, UserLocationState } from '@/types';
import { deviceHeading } from '@/services/deviceHeading';

export function MapMarkers() {
  const { map, isLoaded, userLocation, userLocationState } = useMapStore();
  const { selectedResult, results } = useSearchStore();
  const { waypoints, navigation } = useRoutingStore();

  // User location marker with accuracy circle
  useUserLocationMarker(map, isLoaded, userLocation, userLocationState);

  // Search result markers
  useSearchResultMarkers(map, isLoaded, results, selectedResult);

  // Waypoint markers
  useWaypointMarkers(map, isLoaded, waypoints);

  // Navigation position marker
  useNavigationMarker(map, isLoaded, navigation.isActive, navigation.currentPosition);

  return null;
}

function useUserLocationMarker(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  location: Coordinates | null,
  locationState: UserLocationState | null
) {
  const markerRef = useRef<Marker | null>(null);
  const accuracySourceAdded = useRef(false);
  const deviceHeadingRef = useRef<number | null>(deviceHeading.current);

  useEffect(() => {
    if (!map || !isLoaded) return;

    // Live device-compass heading -> rotate the marker + show the facing cone,
    // even when the user is standing still.
    const unsubHeading = deviceHeading.subscribe((h) => {
      deviceHeadingRef.current = h;
      if (markerRef.current && locationState) {
        updateUserLocationMarkerElement(markerRef.current.getElement(), locationState, h);
      }
    });

    if (location && locationState) {
      // Create or update accuracy circle
      const accuracyInDegrees = metersToDegreesAtLatitude(locationState.accuracy, location.lat);
      const circleGeoJson = createCircleGeoJson(location, accuracyInDegrees);

      if (!accuracySourceAdded.current) {
        // Add accuracy circle source
        if (!map.getSource('user-location-accuracy')) {
          map.addSource('user-location-accuracy', {
            type: 'geojson',
            data: circleGeoJson,
          });

          // Add accuracy circle layer
          map.addLayer({
            id: 'user-location-accuracy-fill',
            type: 'fill',
            source: 'user-location-accuracy',
            paint: {
              'fill-color': '#4285f4',
              'fill-opacity': 0.15,
            },
          });

          map.addLayer({
            id: 'user-location-accuracy-stroke',
            type: 'line',
            source: 'user-location-accuracy',
            paint: {
              'line-color': '#4285f4',
              'line-width': 2,
              'line-opacity': 0.5,
            },
          });

          accuracySourceAdded.current = true;
        }
      } else {
        // Update existing source
        const source = map.getSource('user-location-accuracy') as maplibregl.GeoJSONSource;
        if (source) {
          source.setData(circleGeoJson);
        }
      }

      // Create or update marker
      if (!markerRef.current) {
        const el = createUserLocationMarkerElement(locationState, deviceHeadingRef.current);

        markerRef.current = new Marker({ element: el })
          .setLngLat([location.lng, location.lat])
          .addTo(map);
      } else {
        markerRef.current.setLngLat([location.lng, location.lat]);
        // Update marker element to show heading if available
        updateUserLocationMarkerElement(markerRef.current.getElement(), locationState, deviceHeadingRef.current);
      }
    } else {
      // Remove marker and accuracy circle
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }

      if (accuracySourceAdded.current && map.getSource('user-location-accuracy')) {
        if (map.getLayer('user-location-accuracy-fill')) {
          map.removeLayer('user-location-accuracy-fill');
        }
        if (map.getLayer('user-location-accuracy-stroke')) {
          map.removeLayer('user-location-accuracy-stroke');
        }
        map.removeSource('user-location-accuracy');
        accuracySourceAdded.current = false;
      }
    }

    return () => {
      unsubHeading();
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
      // Clean up accuracy circle on unmount
      if (accuracySourceAdded.current && map) {
        if (map.getLayer('user-location-accuracy-fill')) {
          map.removeLayer('user-location-accuracy-fill');
        }
        if (map.getLayer('user-location-accuracy-stroke')) {
          map.removeLayer('user-location-accuracy-stroke');
        }
        if (map.getSource('user-location-accuracy')) {
          map.removeSource('user-location-accuracy');
        }
        accuracySourceAdded.current = false;
      }
    };
  }, [map, isLoaded, location, locationState]);
}

// Resolve the heading to show: prefer the device compass (works even when
// standing still), fall back to GPS course-over-ground (only valid while moving).
function resolveHeading(locationState: UserLocationState, deviceHeading: number | null): number | null {
  if (deviceHeading !== null && !Number.isNaN(deviceHeading)) return deviceHeading;
  if (locationState.heading !== undefined && locationState.speed && locationState.speed > 0.5) {
    return locationState.heading;
  }
  return null;
}

function createUserLocationMarkerElement(locationState: UserLocationState, deviceHeading: number | null): HTMLElement {
  const el = document.createElement('div');
  el.className = 'user-location-marker';
  el.innerHTML = `
    <div class="user-loc-rot" style="position:relative;width:32px;height:32px;transform:rotate(0deg);transform-origin:center;pointer-events:none">
      <!-- facing-direction cone (apex points where you're facing); hidden until a heading is known -->
      <div class="user-loc-cone" style="position:absolute;left:50%;bottom:55%;transform:translateX(-50%);width:0;height:0;border-left:13px solid transparent;border-right:13px solid transparent;border-bottom:26px solid rgba(26,115,232,0.35);display:none"></div>
      <div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center">
        <div class="absolute inset-0 bg-primary-500 rounded-full opacity-30 animate-ping"></div>
        <div class="w-4 h-4 bg-primary-500 rounded-full border-2 border-white shadow-lg"></div>
      </div>
    </div>
  `;
  applyHeadingToMarker(el, resolveHeading(locationState, deviceHeading));
  return el;
}

function updateUserLocationMarkerElement(el: HTMLElement, locationState: UserLocationState, deviceHeading: number | null): void {
  applyHeadingToMarker(el, resolveHeading(locationState, deviceHeading));
}

// Rotate the marker wrapper to the heading and show/hide the direction cone.
function applyHeadingToMarker(el: HTMLElement, heading: number | null): void {
  const rot = el.querySelector('.user-loc-rot') as HTMLElement | null;
  const cone = el.querySelector('.user-loc-cone') as HTMLElement | null;
  if (rot) rot.style.transform = `rotate(${heading ?? 0}deg)`;
  if (cone) cone.style.display = heading === null ? 'none' : 'block';
}

// Convert meters to approximate degrees at a given latitude
function metersToDegreesAtLatitude(meters: number, latitude: number): number {
  // Earth's radius in meters
  const earthRadius = 6371000;
  // Convert latitude to radians
  const latRad = (latitude * Math.PI) / 180;
  // Meters per degree at this latitude
  const metersPerDegree = (2 * Math.PI * earthRadius * Math.cos(latRad)) / 360;
  return meters / metersPerDegree;
}

// Create a circle GeoJSON polygon
function createCircleGeoJson(center: Coordinates, radiusDegrees: number): GeoJSON.FeatureCollection {
  const points = 64;
  const coordinates: [number, number][] = [];

  for (let i = 0; i <= points; i++) {
    const angle = (i * 360) / points;
    const rad = (angle * Math.PI) / 180;
    coordinates.push([
      center.lng + radiusDegrees * Math.cos(rad),
      center.lat + radiusDegrees * Math.sin(rad),
    ]);
  }

  return {
    type: 'FeatureCollection',
    features: [{
      type: 'Feature',
      properties: {},
      geometry: {
        type: 'Polygon',
        coordinates: [coordinates],
      },
    }],
  };
}

function useSearchResultMarkers(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  results: SearchResult[],
  selectedResult: SearchResult | null
) {
  const markersRef = useRef<Map<string, Marker>>(new Map());

  useEffect(() => {
    if (!map || !isLoaded) return;

    const currentMarkers = markersRef.current;
    const resultIds = new Set(results.map((r) => r.id));

    // Remove markers that are no longer in results
    currentMarkers.forEach((marker, id) => {
      if (!resultIds.has(id)) {
        marker.remove();
        currentMarkers.delete(id);
      }
    });

    // Add or update markers
    results.forEach((result, index) => {
      let marker = currentMarkers.get(result.id);

      if (!marker) {
        const el = createSearchMarkerElement(index + 1, result.id === selectedResult?.id);
        marker = new Marker({ element: el, anchor: 'bottom' })
          .setLngLat([result.coordinates.lng, result.coordinates.lat]);

        const popup = new Popup({ offset: 25, closeButton: false })
          .setHTML(`
            <div class="p-2">
              <div class="font-medium text-gray-900">${result.name}</div>
              <div class="text-sm text-gray-500">${result.address.formatted}</div>
            </div>
          `);

        marker.setPopup(popup);
        marker.addTo(map);
        currentMarkers.set(result.id, marker);
      } else {
        marker.setLngLat([result.coordinates.lng, result.coordinates.lat]);
        updateMarkerSelected(marker.getElement(), result.id === selectedResult?.id);
      }
    });

    return () => {
      currentMarkers.forEach((marker) => marker.remove());
      currentMarkers.clear();
    };
  }, [map, isLoaded, results, selectedResult]);
}

function createSearchMarkerElement(index: number, isSelected: boolean): HTMLElement {
  const el = document.createElement('div');
  el.className = `search-marker ${isSelected ? 'selected' : ''}`;
  el.innerHTML = `
    <div class="relative">
      <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z"
              fill="${isSelected ? '#ea4335' : '#4285f4'}"/>
        <circle cx="16" cy="16" r="8" fill="white"/>
      </svg>
      <span class="absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold ${
        isSelected ? 'text-danger-600' : 'text-primary-600'
      }">${index}</span>
    </div>
  `;
  return el;
}

function updateMarkerSelected(el: HTMLElement, isSelected: boolean): void {
  const svg = el.querySelector('path');
  const span = el.querySelector('span');
  if (svg) {
    svg.setAttribute('fill', isSelected ? '#ea4335' : '#4285f4');
  }
  if (span) {
    span.className = `absolute top-2 left-1/2 -translate-x-1/2 text-xs font-bold ${
      isSelected ? 'text-danger-600' : 'text-primary-600'
    }`;
  }
}

function useWaypointMarkers(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  waypoints: { id: string; coordinates: Coordinates | null; type: string }[]
) {
  const markersRef = useRef<Map<string, Marker>>(new Map());

  useEffect(() => {
    if (!map || !isLoaded) return;

    const currentMarkers = markersRef.current;
    const waypointIds = new Set(waypoints.filter((w) => w.coordinates).map((w) => w.id));

    // Remove markers that are no longer in waypoints
    currentMarkers.forEach((marker, id) => {
      if (!waypointIds.has(id)) {
        marker.remove();
        currentMarkers.delete(id);
      }
    });

    // Add or update markers
    waypoints
      .filter((wp) => wp.coordinates)
      .forEach((wp, index) => {
        let marker = currentMarkers.get(wp.id);
        const coords = wp.coordinates!;

        if (!marker) {
          const el = createWaypointMarkerElement(wp.type, index);
          marker = new Marker({
            element: el,
            anchor: wp.type === 'waypoint' ? 'center' : 'bottom',
            draggable: true,
          }).setLngLat([coords.lng, coords.lat]);

          marker.addTo(map);
          currentMarkers.set(wp.id, marker);
        } else {
          marker.setLngLat([coords.lng, coords.lat]);
          // Reordering (e.g. after "Optimize stops") changes a stop's visiting
          // position but not its coordinates, so refresh the number label here —
          // otherwise the map would show the old order.
          if (wp.type === 'waypoint') {
            const span = marker.getElement().querySelector('span');
            if (span) span.textContent = String(index);
          }
        }
      });

    return () => {
      currentMarkers.forEach((marker) => marker.remove());
      currentMarkers.clear();
    };
  }, [map, isLoaded, waypoints]);
}

function createWaypointMarkerElement(type: string, index: number): HTMLElement {
  const el = document.createElement('div');
  el.className = 'waypoint-marker';

  if (type === 'origin') {
    el.innerHTML = `
      <div class="w-6 h-6 bg-secondary-500 rounded-full border-2 border-white shadow-lg flex items-center justify-center">
        <div class="w-2 h-2 bg-white rounded-full"></div>
      </div>
    `;
  } else if (type === 'destination') {
    el.innerHTML = `
      <svg width="32" height="40" viewBox="0 0 32 40" fill="none">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 24 16 24s16-12 16-24C32 7.163 24.837 0 16 0z" fill="#ea4335"/>
        <circle cx="16" cy="16" r="6" fill="white"/>
      </svg>
    `;
  } else {
    el.innerHTML = `
      <div class="w-5 h-5 bg-white rounded-full border-2 border-primary-500 shadow-lg flex items-center justify-center">
        <span class="text-xs font-bold text-primary-600">${index}</span>
      </div>
    `;
  }

  return el;
}

function useNavigationMarker(
  map: maplibregl.Map | null,
  isLoaded: boolean,
  isNavigating: boolean,
  position: Coordinates | null
) {
  const markerRef = useRef<Marker | null>(null);

  useEffect(() => {
    if (!map || !isLoaded) return;

    if (isNavigating && position) {
      if (!markerRef.current) {
        const el = document.createElement('div');
        el.className = 'navigation-marker';
        el.innerHTML = `
          <div class="w-10 h-10 relative">
            <svg viewBox="0 0 40 40" fill="none" class="w-full h-full">
              <circle cx="20" cy="20" r="18" fill="#4285f4" stroke="white" stroke-width="3"/>
              <path d="M20 8L28 28H12L20 8Z" fill="white"/>
            </svg>
          </div>
        `;

        markerRef.current = new Marker({ element: el, pitchAlignment: 'map' })
          .setLngLat([position.lng, position.lat])
          .addTo(map);
      } else {
        markerRef.current.setLngLat([position.lng, position.lat]);
      }
    } else if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }

    return () => {
      if (markerRef.current) {
        markerRef.current.remove();
        markerRef.current = null;
      }
    };
  }, [map, isLoaded, isNavigating, position]);
}
